import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { HiOutlineViewGrid } from "react-icons/hi";
import { apiGetRequest } from "helpers/Requests";
import { URL_SP_AI_USAGE_BY_FEATURE } from "store/services/URL";
import { formatFeatureLabel } from "./aiUsageLabels";
import {
  buildQuery,
  CHART_FIXED_HEIGHT,
  CHART_WIDGET_HEIGHT,
  colorAt,
  formatAudSmart,
  formatCount,
  getChartDateRange,
  getChartPlotMargin,
  getChartYAxisProps,
  getCountAxisScale,
  getFeatureColumnWidth,
  percentOf,
  truncateLabel,
} from "./aiUsageChartUtils";
import ChartCardHeader from "./ChartCardHeader";
import { ChartMetricStrip, ChartWidgetState } from "./ChartWidgetBody";
import { useChartAxisLayout } from "./useChartAxisLayout";

const FeatureUsageTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <div className="min-w-[210px] rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-xs shadow-lg">
      <p className="font-semibold text-gray-900">{row.name}</p>
      <div className="mt-2 space-y-1.5 text-gray-600">
        <p>
          AI calls:{" "}
          <span className="font-medium text-gray-900">{formatCount(row.value)}</span>
        </p>
        <p>
          Share of calls:{" "}
          <span className="font-medium text-[#4B6CB7]">
            {percentOf(row.value, row.totalCalls)}
          </span>
        </p>
        <p>
          Cost:{" "}
          <span className="font-medium text-gray-900">
            {formatAudSmart(row.cost_aud)}
          </span>
        </p>
        <p>
          Share of spend:{" "}
          <span className="font-medium text-gray-700">
            {percentOf(row.cost_aud, row.totalCost)}
          </span>
        </p>
      </div>
    </div>
  );
};

const FeatureAxisTick = ({ x = 0, y = 0, payload, angle = 0, rows = [] }) => {
  const match = rows.find((row) => row.shortName === payload?.value);
  const fullName = match?.name || payload?.value || "";

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={angle ? 4 : 8}
        textAnchor={angle ? "end" : "middle"}
        fill="#374151"
        fontSize={11}
        transform={angle ? `rotate(${angle})` : undefined}
      >
        <title>{fullName}</title>
        {payload?.value}
      </text>
    </g>
  );
};

const AiUsageByFeatureChart = () => {
  const [period, setPeriod] = useState("week");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);

  const range = useMemo(() => getChartDateRange(period), [period]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    apiGetRequest(
      `${URL_SP_AI_USAGE_BY_FEATURE}${buildQuery({
        from: range.from,
        to: range.to,
      })}`
    )
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data ?? {};
        setItems(Array.isArray(payload?.items) ? payload.items : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Could not load usage by feature."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [range.from, range.to]);

  const totals = useMemo(() => {
    const calls = items.reduce(
      (sum, row) => sum + (Number(row.call_count) || 0),
      0
    );
    const cost = items.reduce(
      (sum, row) => sum + (Number(row.cost_aud) || 0),
      0
    );
    return { calls, cost };
  }, [items]);

  const chartData = useMemo(() => {
    const grouped = new Map();
    items.forEach((row) => {
      const name = formatFeatureLabel(row);
      const calls = Number(row.call_count) || 0;
      const cost = Number(row.cost_aud) || 0;
      const existing = grouped.get(name);
      if (existing) {
        existing.value += calls;
        existing.call_count += calls;
        existing.cost_aud += cost;
        return;
      }
      grouped.set(name, {
        ...row,
        name,
        value: calls,
        call_count: calls,
        cost_aud: cost,
      });
    });

    return [...grouped.values()]
      .sort((a, b) => b.value - a.value)
      .map((row, index) => ({
        ...row,
        shortName: grouped.size <= 2 ? row.name : truncateLabel(row.name, 16),
        color: colorAt(index),
        totalCalls: totals.calls,
        totalCost: totals.cost,
      }));
  }, [items, totals.calls, totals.cost]);

  const columnWidth = getFeatureColumnWidth(chartData.length);
  const { containerRef, compact } = useChartAxisLayout();
  const yAxisScale = useMemo(
    () => getCountAxisScale(chartData.map((row) => row.value), compact),
    [chartData, compact]
  );

  const topFeature = chartData[0];
  const xAxisAngle = chartData.length > 4 ? -32 : chartData.length > 2 ? -18 : 0;

  return (
    <div
      className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      style={{ height: CHART_WIDGET_HEIGHT }}
    >
      <ChartCardHeader
        icon={HiOutlineViewGrid}
        title="Usage by feature"
        subtitle="AI call volume broken down by product feature"
        period={period}
        onPeriodChange={setPeriod}
        dateFrom={range.from}
        dateTo={range.to}
      />

      {!loading && !error && chartData.length > 0 ? (
        <ChartMetricStrip
          items={[
            { label: "Total calls", value: formatCount(totals.calls) },
            { label: "Total spend", value: formatAudSmart(totals.cost) },
            {
              label: "Top feature",
              value: topFeature ? truncateLabel(topFeature.name, 28) : "—",
            },
          ]}
        />
      ) : null}

      <div
        ref={containerRef}
        className="flex min-w-0 flex-1 flex-col px-4 pt-2 pb-4 sm:px-5"
      >
        <ChartWidgetState
          loading={loading}
          error={error}
          empty={chartData.length === 0}
          emptyTitle="No usage in this period"
          emptyHint="Run AI features to see a breakdown by feature here."
          skeletonVariant="bar"
        >
          <div className="min-w-0 w-full" style={{ height: CHART_FIXED_HEIGHT }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={getChartPlotMargin({
                  angledXAxis: Boolean(xAxisAngle),
                  extraRight: 8,
                })}
                barCategoryGap="28%"
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f3f4f6"
                  vertical={false}
                />
                <XAxis
                  dataKey="shortName"
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  height={xAxisAngle ? 56 : 36}
                  tick={(props) => (
                    <FeatureAxisTick {...props} angle={xAxisAngle} rows={chartData} />
                  )}
                />
                <YAxis
                  {...getChartYAxisProps(yAxisScale, { compact })}
                />
                <Tooltip
                  content={<FeatureUsageTooltip />}
                  cursor={{ fill: "rgba(91, 47, 214, 0.05)", radius: 4 }}
                />
                <Bar
                  dataKey="value"
                  radius={[6, 6, 0, 0]}
                  barSize={columnWidth}
                  maxBarSize={columnWidth}
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                  <LabelList
                    dataKey="value"
                    position="top"
                    formatter={(value) => formatCount(value)}
                    style={{
                      fontSize: 10,
                      fill: "#374151",
                      fontWeight: 600,
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartWidgetState>
      </div>
    </div>
  );
};

export default AiUsageByFeatureChart;
